import React from 'react';
import styles from './userJobApplicationsPageStyles.scss';

import Link from '@buyside/components/presentation/Link/link';

import { formatDate } from '@buyside/util/dateHelper';
import { jobVaultPostingUrl } from '@buyside/util/urlHelper';
const { jobApplicationStatuses } = require('buyside/util/constantsFromRails.json');

import { useCurrentUserAccountJobApplicationsQuery } from '@interfaces/graphql-typings';

import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

const statusLookup: { [key: string]: { text: string; style: string } } = {
  [jobApplicationStatuses.pending_approval]: { text: 'Pending...', style: 'italic' },
  [jobApplicationStatuses.viewed]: { text: 'Viewed', style: '300' },
  [jobApplicationStatuses.interviewed]: { text: 'Interview Requested', style: '300' },
  [jobApplicationStatuses.accepted]: { text: 'Accepted', style: '500' },
  [jobApplicationStatuses.rejected]: { text: 'Rejected', style: '500' },
  [jobApplicationStatuses.interviewed_and_rejected]: {
    text: 'Interviewed and Rejected',
    style: '500'
  },
  default: { text: 'Please contact jobs@sumzero.com', style: '500' }
};

export interface ApplicationProps {
  id?: string;
  status?: string;
  createdAt: string;
  jobPosting: {
    id: string;
    title: string;
    employer: {
      aumRange: string;
      fundType: string;
      strategy: string;
    };
    city: string;
  };
}

const UserJobApplicationsPage = (): React.ReactElement => {
  const { loading, data } = useCurrentUserAccountJobApplicationsQuery();

  if (loading || !data?.currentUser) return <LoadingSpinner size={SIZES.LARGE} />;

  const { jobApplications } = data.currentUser;

  const sortedApplications =
    [...(jobApplications || [])].sort((a, b) => {
      if (a?.status === 'rejected' && b?.status !== 'rejected') {
        return 1;
      } else if (a?.status !== 'rejected' && b?.status === 'rejected') {
        return -1;
      }
      return 0;
    }) || [];

  return (
    <>
      <div className={styles.pageTitle}>Jobs Applied To</div>
      {sortedApplications?.length ? (
        <table className={styles.jobAppTable}>
          <tbody>
            <tr>
              <th>Job Posting</th>
              <th>Date Applied</th>
              <th>View Posting</th>
              <th>Status</th>
            </tr>
            {jobApplications?.map(app => {
              if (!app) return null;
              const {
                id: applicationId,
                status,
                createdAt,
                jobPosting: { id, title, employer, city }
              } = app;
              const applicationStatus = statusLookup[status || 'default'];
              return (
                <tr data-testid="application-row" key={applicationId} className={styles.jobList}>
                  <td>
                    <Link to={jobVaultPostingUrl(id)} className={styles.highlightLink}>
                      {title}
                    </Link>
                    <div className={styles.jobDetail}>
                      <div>{employer?.aumRange}</div>
                      <div>
                        {city} {employer?.fundType}
                      </div>
                      <div>{employer?.strategy}</div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.appDate}>{formatDate(createdAt)}</div>
                  </td>
                  <td>
                    {status === 'removed' ? (
                      <div>Posting Removed</div>
                    ) : (
                      <Link to={jobVaultPostingUrl(id)} className={styles.highlightLink}>
                        View Posting
                      </Link>
                    )}
                  </td>
                  <td className={styles[`fontStyle-${applicationStatus.style}`]}>
                    {applicationStatus.text}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className={styles.noContentMsg}>
          <p>You haven't applied to any jobs yet.</p>
        </div>
      )}
    </>
  );
};

export default UserJobApplicationsPage;
